import React, { useState, useEffect, useMemo, useCallback } from 'react';
import GroupChat from '../chat/GroupChat';
import MemberListKakao from './MemberListKakao';
import SuggestionModal from '../chat/SuggestionModal';
import { RoomHeader } from '../tabs/CoordinationTab/components';
import { LogOut, Calendar, Sparkles, Settings, Bell, BellOff, Link, Share2, UserPlus, X, ChevronLeft, CheckCircle2, Circle, Cpu, Megaphone, ChevronUp, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import { useToast } from '../../contexts/ToastContext';
import { useEngine } from '../../hooks/useEngine';
import EngineCandidatesPanel from '../engine/EngineCandidatesPanel';
import DGProfilePanel from '../engine/DGProfilePanel';
import { API_BASE_URL } from '../../config/apiConfig';


const FEAT_AI_MODE    = true;
const FEAT_NICKNAMES  = false;
const FEAT_NOTICES    = false;
const FEAT_ROOM_ALIAS = true;

/**
 * 대화형 조율방 전용 뷰 (Conversational Room View)
 * - 기존의 시간표 그리드 대신 채팅창이 메인
 * - 우측(PC) 또는 탭(모바일)에 멤버 목록 및 정보 표시
 */
const ConversationalRoomView = ({
  currentRoom,
  user,
  isOwner,
  isMobile,
  onManageRoom,
  onBackToRoomList,
  onLeaveRoom,
  onMemberClick,
  onMemberScheduleClick,
  onOpenLogs,
  onFindOptimalTime,
  onRenameRoom,
}) => {
  const { showToast } = useToast();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [friends, setFriends] = useState([]);
  // 방장 여부 확인 (userId 없으면 조원으로 간주)
  const myId = (user?._id || user?.id)?.toString() || '';
  const ownerId = (currentRoom.owner?._id || currentRoom.owner)?.toString() || '';
  const isRoomOwner = !!(myId && ownerId && myId === ownerId);
  // userId 없을 때도 roomId만으로 동작하도록 roomId 기반 키 + userId 보조
  const aiModesKey = `aiModes_${currentRoom._id}_${myId}`;
  const [aiModes, setAiModes] = useState(() => {
    try {
      // 1순위: localStorage에 저장된 값 (한 번이라도 설정한 적 있으면 항상 여기서 로드)
      const saved = localStorage.getItem(aiModesKey);
      if (saved) return JSON.parse(saved);
      // 2순위: 최초 진입 - 방장만 방 생성 시 선택한 모드로, 조원은 전부 OFF
      const defaults = { spellcheck: false, spacing: false, king: false, smart: false };
      if (isRoomOwner && Array.isArray(currentRoom.aiModes)) {
        currentRoom.aiModes.forEach(m => { if (m in defaults) defaults[m] = true; });
      }
      // 초기값을 즉시 저장 → 다음 진입 시 항상 localStorage에서 불러옴
      localStorage.setItem(aiModesKey, JSON.stringify(defaults));
      return defaults;
    } catch { return { spellcheck: false, spacing: false, king: false, smart: false }; }
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // ARES 엔진 상태 (useEngine 훅으로 관리)
  const [showEnginePanel, setShowEnginePanel] = useState(false);
  const [isEnginePanelModify, setIsEnginePanelModify] = useState(false);
  const [overrideCandidates, setOverrideCandidates] = useState(null);
  const [activeSuggestionId, setActiveSuggestionId] = useState(null);
  const [suggRefreshKey, setSuggRefreshKey] = useState(0);
  // memberLocations는 EngineCandidatesPanel에서 지도 열 때 직접 fetch (최신 데이터 보장)
  const { candidates, engineState, isInitialized, isLoading, lastAction, lastActorId, dgStrategy, dgAutoDecision, memberStatuses, isAutoConfirmed, handleCandidatesUpdate, handleStateChange } = useEngine(currentRoom?._id);
  const [showAiSection, setShowAiSection] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [muteToast, setMuteToast] = useState(null);
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [invitePage, setInvitePage] = useState('main'); // 'main' | 'friends'
  const [inviteSelected, setInviteSelected] = useState([]);
  const [inviteMemberIds, setInviteMemberIds] = useState([]);
  const [inviteFriendsLoading, setInviteFriendsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [renameValue, setRenameValue] = useState('');
  // localStorage 키: 방 ID + 내 Firebase UID → 유저마다 다른 키 (같은 브라우저 공유 방지)
  const localNicknamesKey = `roomLocalNicknames_${currentRoom._id}_${auth.currentUser?.uid || ''}`;
  const localRoomAliasKey = `roomNameAlias_${currentRoom._id}_${auth.currentUser?.uid || ''}`;
  // 닉네임 모달: { target: memberData, isSelf: bool }
  const [nicknameModal, setNicknameModal] = useState(null);
  const [nicknameValue, setNicknameValue] = useState('');
  // 내가 남들에게 설정한 닉네임 (localStorage, 나만 보임)
  const [localNicknames, setLocalNicknames] = useState(() => {
    try { return JSON.parse(localStorage.getItem(localNicknamesKey) || '{}'); }
    catch { return {}; }
  });
  // 방 이름 로컬 별칭 (나만 보임)
  const [localRoomAlias, setLocalRoomAlias] = useState(() => {
    try { return localStorage.getItem(localRoomAliasKey) || ''; }
    catch { return ''; }
  });
  // 표시용 방 이름: 로컬 별칭 > 멤버 닉네임 치환 > 서버 이름
  const displayRoomName = useMemo(() => {
    if (localRoomAlias) return localRoomAlias;
    let name = currentRoom.name || '';
    (currentRoom.members || []).forEach(m => {
      const memberId = (m.user?._id || m.user?.id || m.user)?.toString();
      const nickname = localNicknames[memberId] || m.nickname;
      if (nickname) {
        const userData = m.user;
        const realName = typeof userData === 'object'
          ? [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim()
          : '';
        if (realName && name.includes(realName)) {
          name = name.split(realName).join(nickname);
        }
      }
    });
    return name;
  }, [localRoomAlias, currentRoom.name, currentRoom.members, localNicknames]);
  // 현재 로그인 유저가 memberData와 같은 사람인지 Firebase UID로 정확히 판단
  const isMe = (memberData) =>
    auth.currentUser?.uid && (
      auth.currentUser.uid === memberData?.uid ||
      auth.currentUser.uid === memberData?.firebaseUid ||
      (user?._id || user?.id)?.toString() === (memberData?._id || memberData?.id)?.toString() ||
      user?.email === memberData?.email
    );

  // mode 미설정 방(구버전)도 conversational로 취급 (Room 모델 기본값)
  const isConversational = !currentRoom.mode || currentRoom.mode === 'conversational';

  // ARES 패널 자동 오픈
  // CONFIRM/CANCEL 등 서버에서 처리하는 액션은 패널 불필요
  // OPPOSE_TIME / OPPOSE_PLACE 는 cascade 배너(GroupChat)가 처리하므로 ARES 패널 자동 열기 제외
  // ADJUST_TIME: cascade 배너가 처리 → ARES 자동 오픈 불필요
  // PROPOSE_TIME: 스케줄 즉시 변경은 막되, ARES 패널은 열어서 후보 확인 가능하도록
  // PROPOSE_TIME: 즉시 확정은 서버에서 cascadeResult 제거로 방지, ARES는 정상 오픈
  const PANEL_SKIP_ACTIONS = ['MODIFY_TIME', 'CANCEL', 'CONFIRM', 'PASSIVE_ACCEPT', 'OPPOSE_TIME', 'OPPOSE_PLACE', 'ADJUST_TIME', 'ABSTAIN', 'PARTICIPANT_JOINED'];
  useEffect(() => {
    if (lastAction && isConversational) {
      if (PANEL_SKIP_ACTIONS.includes(lastAction.type)) return;
      // 서버 자동 확정(E3-03)이면 ARES 패널 열지 않음
      if (isAutoConfirmed) return;
      setIsEnginePanelModify(false);
      setShowEnginePanel(true);
    }
  }, [lastAction, lastActorId, isConversational, isAutoConfirmed]);

  const handleInvite = () => { setShowDrawer(false); setShowInviteSheet(true); handleOpenFriendsPage(); };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/invite/${currentRoom.inviteCode}`;
    const fallbackCopy = () => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showToast('초대 링크가 복사되었습니다.');
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => showToast('초대 링크가 복사되었습니다.')).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const handleShareLink = () => {
    const url = `${window.location.origin}/invite/${currentRoom.inviteCode}`;
    navigator.share?.({ title: currentRoom.name, text: `${currentRoom.name}에 초대합니다!`, url }).catch(() => {});
  };

  const toggleInvite = (id) =>
    setInviteSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleOpenFriendsPage = async () => {
    setInvitePage('friends');
    setInviteSelected([]);
    setInviteFriendsLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const ids = (data.members || [])
          .map(m => (m.user?._id || m.user?.id || m.user)?.toString())
          .filter(Boolean);
        const ownerId = (data.owner?._id || data.owner?.id || data.owner)?.toString();
        if (ownerId) ids.push(ownerId);
        setInviteMemberIds(ids);
      }
    } catch {}
    setInviteFriendsLoading(false);
  };

  const handleInviteFromSheet = async () => {
    setShowInviteSheet(false);
    setInvitePage('main');
    await handleInviteFriends(inviteSelected);
  };

  const handleInviteFriends = async (selectedIds) => {
    if (selectedIds.length === 0) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_BASE_URL}/api/friends/invite-to-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ inviteCode: currentRoom.inviteCode, friendIds: selectedIds, roomName: currentRoom.name }),
      });
      showToast(`${selectedIds.length}명에게 초대 알림을 보냈습니다.`);
    } catch {
      showToast('초대 전송 중 오류가 발생했습니다.');
    }
  };

  // GroupChat의 + 메뉴 "AI모드" 버튼 이벤트 수신 → 설정 모달 오픈
  useEffect(() => {
    const handler = () => {
      setRenameValue(localRoomAlias || currentRoom.name || '');
      setShowAiSection(Object.values(aiModes).some(Boolean));
      setShowSettingsModal(true);
    };
    window.addEventListener('openAiSettings', handler);
    return () => window.removeEventListener('openAiSettings', handler);
  }, [localRoomAlias, currentRoom.name, aiModes]);

  // ARES 엔진 세션 초기화
  useEffect(() => {
    if (!currentRoom._id || !isConversational) return;
    const initEngine = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        await fetch(`${API_BASE_URL}/api/engine/init-room`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ roomId: currentRoom._id }),
        });
      } catch {}
    };
    initEngine();
  }, [currentRoom._id, isConversational]);

  // 친구 목록 로드
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/friends`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFriends(data.friends || []);
        }
      } catch {}
    };
    fetchFriends();
  }, []);

  const handleAddFriend = async (targetUserId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_BASE_URL}/api/friends/request/${targetUserId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // 친구 목록 갱신
      const res = await fetch(`${API_BASE_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFriends(data.friends || []);
      }
      showToast('친구가 추가되었습니다.');
    } catch {
      showToast('친구 추가 중 오류가 발생했습니다.');
    }
  };

  // (ARES 엔진은 useEngine 훅 + GroupChat의 engineService.sendAction으로 처리)

  // ARES 패널 확정: 선택된 시간/장소로 ScheduleSuggestion 생성 or 기존 일정 수정
  const handleEngineConfirm = useCallback(async ({ time, place }) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;

      // modify 모드인데 suggestionId 없으면 → 방의 활성 일정(future/today) 자동 조회
      let resolvedSuggestionId = activeSuggestionId;
      if (isEnginePanelModify && !resolvedSuggestionId) {
        try {
          const listRes = await fetch(`${API_BASE_URL}/api/chat/${currentRoom._id}/suggestions`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (listRes.ok) {
            // getSuggestions는 배열 직접 반환, date 오름차순 정렬
            const listData = await listRes.json();
            const list = Array.isArray(listData) ? listData : (listData.suggestions || []);
            // 취소/과거 제외한 가장 최근 일정
            const active = [...list].reverse().find(s => s.status !== 'cancelled' && s.status !== 'past');
            if (active) resolvedSuggestionId = active._id;
          }
        } catch {}
      }

      if (resolvedSuggestionId) {
        // 기존 일정 수정
        const updateBody = {};
        if (time && time !== 'undecided' && time.attributes) {
          if (time.attributes.date) updateBody.date = time.attributes.date;
          if (time.attributes.startTime) updateBody.startTime = time.attributes.startTime;
          if (time.attributes.endTime) updateBody.endTime = time.attributes.endTime;
        }
        if (place && place !== 'undecided') {
          const placeAttrs = place.attributes || place;
          const newLocation = placeAttrs.name || placeAttrs.address || '';
          if (newLocation) updateBody.location = newLocation;
        }
        const res = await fetch(`${API_BASE_URL}/api/chat/${currentRoom._id}/suggestions/${resolvedSuggestionId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(updateBody),
        });
        if (res.ok) {
          showToast('일정이 수정되었습니다.');
          window.dispatchEvent(new Event('calendarUpdate'));
        } else {
          const data = await res.json().catch(() => ({}));
          showToast(data.msg || data.error || '일정 수정 중 오류가 발생했습니다.');
        }
      } else {
        // 새 일정 생성 (대화 기반)
        const res = await fetch(`${API_BASE_URL}/api/engine/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ roomId: currentRoom._id, time, place }),
        });
        if (res.ok) {
          showToast('일정이 추가되었습니다.');
          window.dispatchEvent(new Event('calendarUpdate'));
        } else {
          const data = await res.json().catch(() => ({}));
          showToast(data.message || data.error || '일정 추가 중 오류가 발생했습니다.');
        }
      }
    } catch {
      showToast('일정 수정 중 오류가 발생했습니다.');
    }
  }, [currentRoom, activeSuggestionId, isEnginePanelModify, showToast]);

  // 알림 무음 상태 로드
  useEffect(() => {
    const fetchMuteStatus = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/push/mute/${currentRoom._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsMuted(data.isMuted);
        }
      } catch (e) {}
    };
    if (currentRoom._id) fetchMuteStatus();
  }, [currentRoom._id]);

  // 알림 무음 토글
  const handleMuteToggle = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const method = isMuted ? 'DELETE' : 'POST';
      const res = await fetch(`${API_BASE_URL}/api/push/mute/${currentRoom._id}`, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsMuted(data.isMuted);
        setMuteToast(data.isMuted ? '알림이 꺼졌습니다' : '알림이 켜졌습니다');
        setTimeout(() => setMuteToast(null), 2000);
        // 방 목록 localStorage 동기화 (RoomList와 상태 일치)
        try {
          const roomId = currentRoom._id;
          const saved = JSON.parse(localStorage.getItem('mutedRooms') || '[]');
          const next = data.isMuted
            ? (saved.includes(roomId) ? saved : [roomId, ...saved])
            : saved.filter(id => id !== roomId);
          localStorage.setItem('mutedRooms', JSON.stringify(next));
        } catch {}
        // 알림 재활성화 시 push 구독이 만료됐을 수 있으므로 재구독 요청
        // (무음 중 iOS가 subscription을 만료 처리할 수 있음)
        if (!data.isMuted) {
          window.dispatchEvent(new CustomEvent('requestPushResubscribe'));
        }
      }
    } catch (e) {}
  };

  // AI 모드 토글 핸들러
  const handleToggleAiMode = (mode) => {
    setAiModes(prev => {
      const updated = { ...prev, [mode]: !prev[mode] };
      localStorage.setItem(aiModesKey, JSON.stringify(updated));
      return updated;
    });
  };

  // 닉네임 저장
  const handleSaveNickname = async () => {
    if (!nicknameModal) return;
    const val = nicknameValue.trim().slice(0, 30);
    if (nicknameModal.isSelf) {
      try {
        const token = await auth.currentUser?.getIdToken();
        await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}/nickname`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nickname: val }),
        });
      } catch {}
      const myMember = currentRoom.members?.find(m => {
        const mUser = m.user;
        return (mUser?._id || mUser?.id || mUser)?.toString() === (user?._id || user?.id)?.toString()
          || mUser?.email === user?.email
          || mUser?.uid === auth.currentUser?.uid;
      });
      const myId = (myMember?.user?._id || myMember?.user?.id)?.toString();
      if (myId) {
        const updated = { ...localNicknames };
        if (val) updated[myId] = val;
        else delete updated[myId];
        setLocalNicknames(updated);
        localStorage.setItem(localNicknamesKey, JSON.stringify(updated));
      }
    } else {
      const targetId = (nicknameModal.target._id || nicknameModal.target.id)?.toString();
      const updated = { ...localNicknames };
      if (val) updated[targetId] = val;
      else delete updated[targetId];
      setLocalNicknames(updated);
      localStorage.setItem(localNicknamesKey, JSON.stringify(updated));
    }
    window.dispatchEvent(new CustomEvent('roomAliasChanged'));
    setNicknameModal(null);
  };

  // ──── 공지 상태 ────
  const [notices, setNotices] = useState([]);
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [showNoticeWrite, setShowNoticeWrite] = useState(false);
  const [noticeWriteText, setNoticeWriteText] = useState('');

  // 공지 목록 로드
  useEffect(() => {
    if (!FEAT_NOTICES || !currentRoom._id) return;
    const fetchNotices = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}/notices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setNotices(data || []);
        }
      } catch {}
    };
    fetchNotices();
  }, [currentRoom._id]);

  // 공지 추가
  const handleAddNotice = async () => {
    const text = noticeWriteText.trim();
    if (!text) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}/notices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        const updated = await res.json();
        setNotices(updated);
        setNoticeIndex(0);
      }
    } catch {}
    setNoticeWriteText('');
    setShowNoticeWrite(false);
  };

  // 공지 삭제
  const handleDeleteNotice = async (noticeId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/coordination/rooms/${currentRoom._id}/notices/${noticeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const updated = await res.json();
        setNotices(updated);
        setNoticeIndex(0);
      }
    } catch {}
  };

  // 항상 4가지 모드 전부 표시
  const availableModes = ['spellcheck', 'spacing', 'king', 'smart'];

  const AI_MODE_LABELS = {
    spellcheck: { label: '맞춤법 교정', desc: '전송 전 맞춤법 자동 교정', activeColor: '#1d4ed8', activeBg: '#dbeafe', activeBorder: '#93c5fd' },
    spacing:    { label: '띄어쓰기 교정', desc: '전송 전 띄어쓰기 자동 교정', activeColor: '#15803d', activeBg: '#dcfce7', activeBorder: '#86efac' },
    king:       { label: '왕모드', desc: '받은 메시지를 왕실 경어체로 변환', activeColor: '#7c3aed', activeBg: '#ede9fe', activeBorder: '#c4b5fd' },
    smart:      { label: '스마트 인식', desc: '"물음표"→? "줄바꿈"→↵ 문맥 기반 자동 변환', activeColor: '#b45309', activeBg: '#fef3c7', activeBorder: '#fcd34d' },
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-gray-50 relative">
      {/* 헤더 (기존 컴포넌트 재사용) */}
      <RoomHeader
        currentRoom={currentRoom}
        user={user}
        isOwner={isOwner}
        onBackToRoomList={onBackToRoomList}
        isMobile={isMobile}
        onToggleDrawer={() => setShowDrawer(!showDrawer)}
        onToggleSearch={() => { setIsSearching(prev => !prev); setSearchQuery(''); }}
        isSearching={isSearching}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onRenameRoom={onRenameRoom}
        displayRoomName={displayRoomName}
      />

      {/* ──── 카카오톡 스타일 공지 배너 ──── */}
      {FEAT_NOTICES && notices.length > 0 && (
        <div
          style={{ background: '#FEF9C3', borderBottom: '1px solid #FDE047', flexShrink: 0, cursor: 'pointer' }}
          className="flex items-center px-3 py-2 gap-2"
          onClick={() => setShowNoticeModal(true)}
        >
          {/* 메가폰 아이콘 */}
          <div style={{ width: 28, height: 28, background: '#EAB308', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Megaphone size={15} color="#fff" />
          </div>
          {/* 공지 텍스트 */}
          <span style={{ flex: 1, fontSize: 13, color: '#713F12', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {notices[noticeIndex]?.text}
          </span>
          {/* N/M 표시 + 위아래 버튼 */}
          {notices.length > 1 && (
            <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>{noticeIndex + 1}/{notices.length}</span>
              <button
                onClick={e => { e.stopPropagation(); setNoticeIndex(i => (i - 1 + notices.length) % notices.length); }}
                style={{ padding: 2, color: '#92400E' }}
              ><ChevronUp size={14} /></button>
              <button
                onClick={e => { e.stopPropagation(); setNoticeIndex(i => (i + 1) % notices.length); }}
                style={{ padding: 2, color: '#92400E' }}
              ><ChevronDown size={14} /></button>
            </div>
          )}
          {/* 공지 추가 버튼 */}
          <button
              onClick={e => { e.stopPropagation(); setNoticeWriteText(''); setShowNoticeWrite(true); }}
              style={{ padding: 4, color: '#92400E', flexShrink: 0 }}
              title="공지 추가"
            ><Plus size={15} /></button>
        </div>
      )}
      {/* 공지 없을 때 모든 멤버에게 보이는 추가 버튼 */}
      {FEAT_NOTICES && notices.length === 0 && (
        <div
          style={{ background: '#FFFBEB', borderBottom: '1px solid #FEF3C7', flexShrink: 0, cursor: 'pointer' }}
          className="flex items-center px-3 py-2 gap-2"
          onClick={() => { setNoticeWriteText(''); setShowNoticeWrite(true); }}
        >
          <div style={{ width: 28, height: 28, background: '#D1D5DB', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Megaphone size={15} color="#fff" />
          </div>
          <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 400 }}>공지를 등록해보세요</span>
          <Plus size={14} color="#9CA3AF" style={{ marginLeft: 'auto' }} />
        </div>
      )}

      {/* ARES 상태 바 - 비활성화 */}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* 메인 영역: 그룹 채팅 */}
        <div className="flex-1 min-w-0 flex flex-col relative">
          <GroupChat
            roomId={currentRoom._id}
            user={user}
            isMobile={isMobile}
            aiModes={aiModes}
            searchQuery={isSearching ? searchQuery : ''}
            hideScrollBtn={showInviteSheet}
            localNicknames={localNicknames}
            roomMembers={currentRoom.members}
            onAddNotice={FEAT_NOTICES ? (text) => {
              setNoticeWriteText(text);
              setShowNoticeWrite(true);
            } : null}
            onEngineCandidatesUpdate={isConversational ? handleCandidatesUpdate : undefined}
            onEngineStateChange={isConversational ? handleStateChange : undefined}
            onSuggestionUpdated={isConversational ? (sid, data) => {
              const status = data?.status;
              if (status === 'created') {
                // 새 제안 생성 (불참 재구성): ARES 패널이 열려있으면 새 제안으로 전환
                if (activeSuggestionId) {
                  setActiveSuggestionId(sid);
                  setSuggRefreshKey(k => k + 1);
                }
              } else if (sid && activeSuggestionId && sid === activeSuggestionId) {
                setSuggRefreshKey(k => k + 1);
              }
            } : undefined}
            onAresSuggestionClick={isConversational ? async (suggestionId, cascadeData) => {
              setIsEnginePanelModify(true);
              setShowEnginePanel(true);
              setActiveSuggestionId(suggestionId || null);

              // cascade 대안이 있으면 cascadeResult.alternatives를 ARES 패널에 직접 세팅
              if (!suggestionId && cascadeData?.cascadeResult?.alternatives?.length > 0) {
                const alts = cascadeData.cascadeResult.alternatives;
                // 타입에 따라 time / place pool로 분리
                // C-1 수정: type 필터를 robust하게 (server가 'place'/'place_candidate' 혼용할 수 있음)
                const placeAlts = alts.filter(a => a.type?.includes('place') || (!a.attributes?.startTime && a.attributes?.name));
                const timeAlts = alts.filter(a => !placeAlts.includes(a));
                // C-1 수정: time 후보 없을 때 기존 세션 candidates.time으로 fallback
                setOverrideCandidates({
                  time: timeAlts.length > 0
                    ? timeAlts.map(a => ({
                        id: a.candidateId,
                        type: 'time_candidate',
                        attributes: a.attributes,
                        score: a.score ?? 0.8,
                        isCurrent: false,
                      }))
                    : (candidates?.time || []),
                  place: placeAlts.length > 0
                    ? placeAlts.map(a => ({
                        id: a.candidateId,
                        type: 'place_candidate',
                        attributes: a.attributes,
                        score: a.score ?? 0.8,
                        isCurrent: false,
                      }))
                    : (candidates?.place || []),
                  person: [],
                });
                return;
              }

              if (suggestionId) {
                try {
                  const token = await auth.currentUser?.getIdToken();
                  const res = await fetch(`${API_BASE_URL}/api/engine/candidates-from-suggestion`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ suggestionId, roomId: currentRoom._id }),
                  });
                  const data = await res.json();
                  if (data.candidates) {
                    // Bug 7 수정: 서버에서 저장된 aiCandidates.place(생성 시점 top3) 직접 사용
                    // 이전 코드는 현재 세션 candidates.place(라이브 상태)를 재활용해 원본 top3와 달라지는 버그
                    setOverrideCandidates({
                      time: data.candidates.time,
                      place: data.candidates.place,
                      person: [],
                    });
                  }
                } catch (e) {
                  console.warn('[Engine] candidates-from-suggestion failed:', e.message);
                }
              }
            } : undefined}
          />
        </div>
      </div>

      {/* ARES 엔진 추천 현황 패널 (루트 레벨 - fixed 포지션으로 전체 화면 오버레이) */}
      {showEnginePanel && (
        <EngineCandidatesPanel
          candidates={overrideCandidates || candidates}
          engineState={engineState}
          lastAction={lastAction}
          isInitialized={isInitialized}
          isLoading={isLoading}
          isMobile={isMobile}
          dgStrategy={dgStrategy}
          dgAutoDecision={dgAutoDecision}
          memberStatuses={memberStatuses}
          roomId={currentRoom._id}
          isModifyMode={isEnginePanelModify}
          activeSuggestionId={activeSuggestionId}
          suggRefreshKey={suggRefreshKey}
          onClose={() => { setShowEnginePanel(false); setIsEnginePanelModify(false); setOverrideCandidates(null); setActiveSuggestionId(null); setSuggRefreshKey(0); }}
          onConfirm={handleEngineConfirm}
        />
      )}

      {/* 일정 관리 모달 */}
      <SuggestionModal
        isOpen={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        roomId={currentRoom._id}
        isMobile={isMobile}
      />

      {/* 무음 토스트 */}
      {muteToast && (
        <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:max-w-xs z-[60] px-4 py-3 rounded-lg shadow-lg bg-green-500 text-white text-sm font-medium animate-slide-down">
          {muteToast}
        </div>
      )}

      {/* 초대 시트 (링크복사/링크공유/친구초대 → 슬라이드로 친구 선택) */}
      {showInviteSheet && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-[55]"
            onClick={() => { setShowInviteSheet(false); setInvitePage('main'); }}
          />
          <div
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[56] pb-safe"
            style={{ overflow: 'hidden' }}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* 슬라이딩 컨테이너 (absolute 방식 → 각 페이지 높이 독립) */}
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              {/* 페이지 1: 초대 방법 선택 */}
              <div style={{
                position: invitePage !== 'main' ? 'absolute' : 'relative',
                top: 0, left: 0, right: 0, width: '100%',
                transform: invitePage === 'friends' ? 'translateX(-100%)' : 'translateX(0)',
                transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
              }}>
                <div className="flex justify-between items-center px-4 py-3 border-b">
                  <h3 className="text-base font-bold text-gray-800">초대하기</h3>
                  <button onClick={() => setShowInviteSheet(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                    <X size={20} />
                  </button>
                </div>
                <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={handleCopyLink} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#f8fafc', borderRadius: 14, border: 'none', cursor: 'pointer' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Link size={20} color="#2563eb" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>링크 복사</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>초대 링크를 클립보드에 복사</div>
                    </div>
                  </button>
                  {typeof navigator.share === 'function' && (
                    <button onClick={handleShareLink} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#f8fafc', borderRadius: 14, border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Share2 size={20} color="#16a34a" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>링크 공유</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>카카오톡, 문자 등으로 공유</div>
                      </div>
                    </button>
                  )}
                  <button onClick={handleOpenFriendsPage} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#f8fafc', borderRadius: 14, border: 'none', cursor: 'pointer' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <UserPlus size={20} color="#7c3aed" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>친구 초대</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>친구 목록에서 선택해서 초대</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* 페이지 2: 친구 선택 */}
              <div style={{
                position: invitePage !== 'friends' ? 'absolute' : 'relative',
                top: 0, left: 0, right: 0, width: '100%',
                display: 'flex', flexDirection: 'column',
                transform: invitePage === 'friends' ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
              }}>
                <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
                  <h3 className="text-base font-bold text-gray-800">친구 초대</h3>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={handleCopyLink} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#dbeafe', border: 'none', borderRadius: 20, cursor: 'pointer' }}>
                      <Link size={14} color="#2563eb" />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb' }}>링크 복사</span>
                    </button>
                    <button onClick={handleShareLink} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: '#dcfce7', border: 'none', borderRadius: 20, cursor: 'pointer' }}>
                      <Share2 size={14} color="#16a34a" />
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>링크 공유</span>
                    </button>
                    <button onClick={() => { setShowInviteSheet(false); setInvitePage('main'); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                      <X size={20} />
                    </button>
                  </div>
                </div>
                {/* 친구 목록 (5명 고정 높이 + 스크롤) */}
                <div style={{ height: '290px', overflowY: 'auto', padding: '8px' }}>
                  {inviteFriendsLoading ? (
                    <div className="flex justify-center py-10">
                      <div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    </div>
                  ) : friends.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">
                      <UserPlus size={32} style={{ margin: '0 auto 8px', display: 'block' }} />
                      <p className="text-sm">친구가 없습니다.</p>
                    </div>
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {friends.map(f => {
                        const id = f._id || f.id;
                        const isAlreadyIn = inviteMemberIds.includes(id?.toString());
                        const isSel = inviteSelected.includes(id);
                        const initial = (f.firstName?.[0] || f.email?.[0] || '?').toUpperCase();
                        return (
                          <li
                            key={id}
                            onClick={() => { if (!isAlreadyIn) toggleInvite(id); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '10px 12px', borderRadius: 8,
                              cursor: isAlreadyIn ? 'default' : 'pointer',
                              background: isAlreadyIn ? '#f0fdf4' : isSel ? '#eff6ff' : 'transparent',
                              transition: 'background 0.15s',
                            }}
                          >
                            <div style={{
                              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                              background: isAlreadyIn ? '#bbf7d0' : isSel ? '#3b82f6' : '#dbeafe',
                              color: isAlreadyIn ? '#16a34a' : isSel ? '#fff' : '#1d4ed8',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 700, fontSize: 14, overflow: 'hidden',
                            }}>
                              {f.profileImage
                                ? <img src={f.profileImage} alt={initial} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : initial}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, color: isAlreadyIn ? '#15803d' : '#111827' }}>
                                {f.firstName}{f.lastName}
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {isAlreadyIn ? '이미 방에 있음' : f.email}
                              </div>
                            </div>
                            <div style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${isAlreadyIn ? '#16a34a' : isSel ? '#3b82f6' : '#d1d5db'}`,
                              background: isAlreadyIn ? '#16a34a' : isSel ? '#3b82f6' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {(isAlreadyIn || isSel) && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                  <button
                    onClick={() => { setShowInviteSheet(false); setInvitePage('main'); }}
                    style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
                  >
                    건너뛰기
                  </button>
                  <button
                    onClick={handleInviteFromSheet}
                    disabled={inviteSelected.length === 0}
                    style={{
                      flex: 2, padding: '10px',
                      background: inviteSelected.length > 0 ? '#3b82f6' : '#93c5fd',
                      border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                      cursor: inviteSelected.length > 0 ? 'pointer' : 'not-allowed', color: '#fff',
                    }}
                  >
                    초대{inviteSelected.length > 0 ? ` (${inviteSelected.length}명)` : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 방 설정 팝업 (방 이름 변경 + AI 선택 통합) */}
      {showSettingsModal && (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowSettingsModal(false); }}
        >
          <div
            className="bg-white w-full flex flex-col overflow-hidden"
            style={{ borderRadius: 24, maxWidth: 420, maxHeight: '88vh', boxShadow: '0 8px 48px rgba(0,0,0,0.22)' }}
          >
            {/* 헤더 */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>방 설정</h2>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0, marginTop: 2 }}>방 이름 변경 및 AI 모드 설정</p>
              </div>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} color="#6b7280" />
              </button>
            </div>

            {/* 스크롤 영역 */}
            <div className="flex-1 overflow-y-auto px-5" style={{ paddingBottom: 8 }}>
              {/* 방 이름 */}
              {FEAT_ROOM_ALIAS && <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  방 이름 <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>(나만 보임)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      border: '2px solid #e5e7eb', borderRadius: 14, padding: '12px 48px 12px 16px',
                      fontSize: 15, fontWeight: 500, color: '#111827', background: '#f9fafb',
                      outline: 'none', transition: 'border-color 0.15s',
                    }}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value.slice(0, 30))}
                    placeholder="방 이름을 입력하세요"
                    maxLength={30}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                  <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>
                    {renameValue.length}/30
                  </span>
                </div>
              </div>}

              {/* AI 모드 토글 버튼 */}
              {FEAT_AI_MODE && <div style={{ marginBottom: 12 }}>
                <button
                  onClick={() => {
                    const anyActive = Object.values(aiModes).some(Boolean);
                    if (anyActive) {
                      // 하나라도 켜져 있으면 → 전부 끄기 + 섹션 닫기
                      const allOff = { spellcheck: false, spacing: false, king: false, smart: false };
                      setAiModes(allOff);
                      localStorage.setItem(aiModesKey, JSON.stringify(allOff));
                      setShowAiSection(false);
                    } else {
                      // 전부 꺼져 있으면 → 맞춤법 교정 디폴트 ON + 섹션 펼치기
                      const defaultOn = { spellcheck: true, spacing: false, king: false, smart: false };
                      setAiModes(defaultOn);
                      localStorage.setItem(aiModesKey, JSON.stringify(defaultOn));
                      setShowAiSection(true);
                    }
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: Object.values(aiModes).some(Boolean) ? '#f5f3ff' : '#f9fafb',
                    border: `2px solid ${Object.values(aiModes).some(Boolean) ? '#c4b5fd' : '#e5e7eb'}`,
                    borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 10,
                      background: Object.values(aiModes).some(Boolean) ? '#7c3aed' : '#e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.2s',
                    }}>
                      <Sparkles size={17} color={Object.values(aiModes).some(Boolean) ? '#fff' : '#9ca3af'} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: Object.values(aiModes).some(Boolean) ? '#6d28d9' : '#374151' }}>AI 모드</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                        {Object.values(aiModes).some(Boolean)
                          ? `${Object.values(aiModes).filter(Boolean).length}개 활성화됨`
                          : '맞춤법, 띄어쓰기, 왕모드'}
                      </div>
                    </div>
                  </div>
                  {/* 토글 스위치 - 실제 모드 활성화 여부 기준 */}
                  <div style={{
                    width: 46, height: 26, borderRadius: 13, flexShrink: 0,
                    background: Object.values(aiModes).some(Boolean) ? '#7c3aed' : '#d1d5db',
                    position: 'relative', transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3,
                      left: Object.values(aiModes).some(Boolean) ? 23 : 3,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    }} />
                  </div>
                </button>

                {/* AI 모드 카드들 */}
                {showAiSection && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {availableModes.map(mode => {
                      const info = AI_MODE_LABELS[mode];
                      if (!info) return null;
                      const isOn = aiModes[mode];
                      return (
                        <button
                          key={mode}
                          onClick={() => handleToggleAiMode(mode)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                            padding: '11px 14px',
                            background: isOn ? info.activeBg : '#f9fafb',
                            border: `2px solid ${isOn ? info.activeBorder : '#e5e7eb'}`,
                            borderRadius: 12, cursor: 'pointer', transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: isOn ? info.activeColor : '#374151' }}>{info.label}</div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{info.desc}</div>
                          </div>
                          {isOn
                            ? <CheckCircle2 size={20} color={info.activeColor} style={{ flexShrink: 0 }} />
                            : <Circle size={20} color="#d1d5db" style={{ flexShrink: 0 }} />
                          }
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>}
            </div>

            {/* 하단 버튼 */}
            <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 10, borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{
                  flex: 1, padding: '13px 0', borderRadius: 14,
                  border: '2px solid #e5e7eb', background: '#fff',
                  fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={() => {
                  const name = renameValue.trim();
                  // 클릭 시점에 uid를 직접 읽어야 함 (초기 렌더 시 auth 미초기화 문제 방지)
                  const currentUid = auth.currentUser?.uid || '';
                  const key = `roomNameAlias_${currentRoom._id}_${currentUid}`;
                  if (name) {
                    localStorage.setItem(key, name);
                    setLocalRoomAlias(name);
                  } else {
                    localStorage.removeItem(key);
                    setLocalRoomAlias('');
                  }
                  // AI 모드 명시적 저장 (토글 시 실시간 저장되지만 저장 버튼에서도 확정)
                  localStorage.setItem(aiModesKey, JSON.stringify(aiModes));
                  // 방 목록 카드들이 즉시 갱신되도록 이벤트 발송
                  window.dispatchEvent(new CustomEvent('roomAliasChanged'));
                  setShowSettingsModal(false);
                }}
                style={{
                  flex: 2, padding: '13px 0', borderRadius: 14,
                  background: '#3b82f6', border: 'none',
                  fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 닉네임 변경 팝업 */}
      {FEAT_NICKNAMES && nicknameModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setNicknameModal(null); }}
        >
          <div
            className="bg-white w-full flex flex-col overflow-hidden"
            style={{ borderRadius: 24, maxWidth: 360, boxShadow: '0 8px 48px rgba(0,0,0,0.22)' }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>
                  {nicknameModal.isSelf ? '내 닉네임 설정' : '표시 이름 설정'}
                </h2>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0, marginTop: 2 }}>
                  {nicknameModal.isSelf
                    ? '이 방에서 다른 멤버들에게 보이는 이름'
                    : '나한테만 보이는 이름 (다른 멤버에게는 영향 없음)'}
                </p>
              </div>
              <button
                onClick={() => setNicknameModal(null)}
                style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} color="#6b7280" />
              </button>
            </div>
            <div className="px-5 pb-3">
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    border: '2px solid #e5e7eb', borderRadius: 14, padding: '12px 48px 12px 16px',
                    fontSize: 15, fontWeight: 500, color: '#111827', background: '#f9fafb',
                    outline: 'none', transition: 'border-color 0.15s',
                  }}
                  value={nicknameValue}
                  onChange={(e) => setNicknameValue(e.target.value.slice(0, 30))}
                  placeholder="닉네임을 입력하세요 (비우면 원래 이름)"
                  maxLength={30}
                  autoFocus
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNickname(); if (e.key === 'Escape') setNicknameModal(null); }}
                />
                <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>
                  {nicknameValue.length}/30
                </span>
              </div>
            </div>
            <div style={{ padding: '4px 20px 20px', display: 'flex', gap: 10 }}>
              <button
                onClick={() => setNicknameModal(null)}
                style={{ flex: 1, padding: '13px 0', borderRadius: 14, border: '2px solid #e5e7eb', background: '#fff', fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}
              >
                취소
              </button>
              <button
                onClick={handleSaveNickname}
                style={{ flex: 2, padding: '13px 0', borderRadius: 14, background: '#3b82f6', border: 'none', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer' }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──── 공지 전체보기 팝업 ──── */}
      {FEAT_NOTICES && showNoticeModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setShowNoticeModal(false); }}
        >
          <div className="bg-white w-full flex flex-col" style={{ borderRadius: 20, maxWidth: 420, maxHeight: '80vh', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            {/* 헤더 */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <div style={{ width: 32, height: 32, background: '#EAB308', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Megaphone size={17} color="#fff" />
                </div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>공지 {notices.length}개</h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                    onClick={() => { setShowNoticeModal(false); setNoticeWriteText(''); setShowNoticeWrite(true); }}
                    style={{ padding: '6px 12px', background: '#EAB308', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Plus size={13} />공지 추가
                  </button>
                <button onClick={() => setShowNoticeModal(false)} style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={16} color="#6b7280" />
                </button>
              </div>
            </div>
            {/* 공지 목록 */}
            <div className="flex-1 overflow-y-auto px-4 pb-5" style={{ gap: 10, display: 'flex', flexDirection: 'column' }}>
              {notices.length === 0 && (
                <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, marginTop: 24 }}>등록된 공지가 없습니다.</p>
              )}
              {[...notices].reverse().map((n, i) => (
                <div key={n._id} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '12px 14px', position: 'relative' }}>
                  <p style={{ fontSize: 14, color: '#78350F', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.text}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span style={{ fontSize: 11, color: '#A16207' }}>
                      {n.createdAt ? new Date(n.createdAt).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                    <button
                        onClick={() => handleDeleteNotice(n._id)}
                        style={{ padding: 4, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 12 }}
                      >
                        <Trash2 size={13} />삭제
                      </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ──── 공지 작성 팝업 ──── */}
      {FEAT_NOTICES && showNoticeWrite && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setShowNoticeWrite(false); }}
        >
          <div className="bg-white w-full flex flex-col" style={{ borderRadius: 20, maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>공지 등록</h2>
              <button onClick={() => setShowNoticeWrite(false)} style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} color="#6b7280" />
              </button>
            </div>
            <div className="px-5 pb-3">
              <textarea
                style={{
                  width: '100%', boxSizing: 'border-box', height: 120,
                  border: '2px solid #e5e7eb', borderRadius: 14, padding: '12px 16px',
                  fontSize: 14, color: '#111827', background: '#f9fafb',
                  outline: 'none', resize: 'none', lineHeight: 1.6,
                }}
                value={noticeWriteText}
                onChange={e => setNoticeWriteText(e.target.value.slice(0, 500))}
                placeholder="공지 내용을 입력하세요 (최대 500자)"
                autoFocus
                onFocus={e => e.target.style.borderColor = '#EAB308'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{noticeWriteText.length}/500</div>
            </div>
            <div style={{ padding: '4px 20px 20px', display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowNoticeWrite(false)}
                style={{ flex: 1, padding: '13px 0', borderRadius: 14, border: '2px solid #e5e7eb', background: '#fff', fontSize: 14, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}
              >취소</button>
              <button
                onClick={handleAddNotice}
                style={{ flex: 2, padding: '13px 0', borderRadius: 14, background: '#EAB308', border: 'none', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer' }}
              >등록</button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 오른쪽 Drawer (조원 목록 + 메뉴) */}
      <>
        {/* Overlay */}
        {showDrawer && (
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity animate-fadeIn"
            onClick={() => setShowDrawer(false)}
          />
        )}

        {/* Drawer Panel */}
        <div className={`
          fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          ${showDrawer ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}>
          {/* Drawer Header: 메뉴 | 설정(방장, 표준모드만) + 알림 + 나가기 */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-800">메뉴</h3>
            <div className="flex items-center gap-1">
              {/* 방 관리 (표준 모드 + 방장만) */}
              {isOwner && !isConversational && (
                <button
                  onClick={() => {
                    setShowDrawer(false);
                    onManageRoom();
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
                  title="방 관리"
                >
                  <Settings size={20} />
                </button>
              )}
              {/* 알림 무음 토글 */}
              <button
                onClick={handleMuteToggle}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
                title={isMuted ? '알림 켜기' : '알림 끄기'}
              >
                {isMuted ? <BellOff size={20} /> : <Bell size={20} />}
              </button>
              {/* 방 나가기 */}
              <button
                onClick={() => {
                  setShowDrawer(false);
                  onLeaveRoom();
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors"
                title="방 나가기"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>

          {/* Drawer Content: 조원 목록 */}
          <div className="flex-1 overflow-hidden">
            <MemberListKakao
              currentRoom={currentRoom}
              user={user}
              isOwner={isOwner}
              onMemberClick={onMemberClick}
              onMemberScheduleClick={onMemberScheduleClick}
              onInvite={handleInvite}
              friends={friends}
              onAddFriend={handleAddFriend}
              localNicknames={localNicknames}
              onNicknameClick={FEAT_NICKNAMES ? (memberData) => {
                setShowDrawer(false);
                // Firebase UID로 정확히 자신인지 판단 (isCurrentUser는 user.id 미스매치 가능)
                const actualIsSelf = isMe(memberData);
                const targetId = (memberData._id || memberData.id)?.toString();
                const defaultName = `${memberData.firstName || ''}${memberData.lastName || ''}`.trim() || '';
                const currentNick = actualIsSelf
                  ? (currentRoom.members?.find(m => (m.user?._id || m.user)?.toString() === targetId)?.nickname || defaultName)
                  : (localNicknames[targetId] || defaultName);
                setNicknameValue(currentNick);
                setNicknameModal({ target: memberData, isSelf: actualIsSelf });
              } : null}
            />
          </div>

          {/* DG Engine 성향 프로파일 패널 */}
          <div className="px-3 py-2">
            <DGProfilePanel userId={auth.currentUser?.uid} />
          </div>

          {/* Drawer Footer: 아이콘 버튼들 (3개 균등 배치) */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="grid grid-cols-3 gap-2">
              {/* 일정 관리 */}
              <button
                onClick={() => {
                  setShowDrawer(false);
                  setShowSuggestions(true);
                }}
                className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex flex-col items-center gap-1"
                title="일정 관리"
              >
                <Calendar size={18} />
                <span className="text-[10px] font-medium whitespace-nowrap">일정</span>
              </button>

              {/* 최적 시간 찾기 (비활성화) */}
              <button
                disabled
                className="p-2 bg-gray-50 text-gray-300 rounded-lg flex flex-col items-center gap-1 cursor-not-allowed"
                title="최적화"
              >
                <Calendar size={18} />
                <span className="text-[10px] font-medium whitespace-nowrap">최적화</span>
              </button>

              {/* ARES 추천 - 비활성화 */}

              {/* 방 설정 (방 이름 변경 + AI 모드 통합) */}
              <button
                onClick={() => { setShowDrawer(false); setRenameValue(localRoomAlias || currentRoom.name || ''); setShowAiSection(Object.values(aiModes).some(Boolean)); setShowSettingsModal(true); }}
                className={`p-2 rounded-lg transition-colors flex flex-col items-center gap-1 ${
                  Object.values(aiModes).some(Boolean)
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
                title="방 설정"
              >
                <Settings size={18} />
                <span className="text-[10px] font-medium whitespace-nowrap">방 설정</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default ConversationalRoomView;
